import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import type SessionService from 'teacher-ui-client/services/session'
import type Student from 'teacher-ui-client/models/student'
import type Assignment from 'teacher-ui-client/models/assignment'
import { DataProcessor } from '@blakeelearning/data-tables/utils/data-processor'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { SortColumnIcon } from 'teacher-ui-client/components/sort-column-icon'
import { t, formatDate, type IntlService } from 'ember-intl'
import { AssignmentStatus } from 'teacher-ui-client/components/assignments-admin/assignment-status'
import { ActivityDescriptionLesson } from 'teacher-ui-client/components/assignments-admin/activity-description/lesson-activity'
import { ActivityDescriptionBook } from 'teacher-ui-client/components/assignments-admin/activity-description/book-activity'
import type Store from '@ember-data/store'
import { colspanMax } from 'teacher-ui-client/modifiers/colspan-max'
import type Owner from '@ember/owner'
import type Teacher from 'teacher-ui-client/models/teacher'

interface Signature {
  Element: HTMLDivElement
  Args: {
    assignmentsForSelectedStudent: Assignment[]
    student: Student
  }
}

interface StudentTableRowData {
  assignment: Assignment
  assignerName: string
  studentStatus: string
}

/**
 * Table of assignment details for a particular student
 */
export class InnerAssignmentsTable extends Component<Signature> {
  @service intl!: IntlService

  @service store!: Store

  @service session!: SessionService

  @tracked sortingPriorityQueue = []

  processor: DataProcessor<StudentTableRowData>

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.processor = new DataProcessor(this)
  }

  get data(): StudentTableRowData[] {
    const teachers = this.store.peekAll<Teacher>('teacher')
    return this.args.assignmentsForSelectedStudent.map((assignment) => {
      // do not want the status of the entire assignment but rather this students' status for the assignment
      const studentStatus =
        assignment.assignedStudents?.find((as) => String(as.canonical_student_id) === String(this.args.student.id))
          ?.status ?? ''
      const assigner = teachers.find((t) => t.id === String(assignment.assignerId))
      let assignerName = String(assignment.assignerId)
      if (assigner) {
        if (this.session.currentTeacher.id === assigner.id) {
          assignerName = this.intl.t('assignments.me')
        } else {
          assignerName = assigner.fullName
        }
      }

      return { assignment, studentStatus, assignerName }
    })
  }

  <template>
    <div class="w-full overflow-auto" data-test-inner-assignments-table ...attributes>
      {{#let this.processor.transformedData this.processor.tableAction as |transformedData|}}
        <table class="w-full divide-y divide-white">
          <thead>
            <tr class="divide-x divide-white">
              <th class="bg-juicy-orange-100 p-3">
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.title"}}
                  </span>
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "assignerName")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.assignedBy"}}
                  </span>
                  <SortColumnIcon @columnName="assignerName" @sortingPriorityQueue={{this.sortingPriorityQueue}} />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 p-3"
                {{on "click" (fn this.processor.tableActions.sortByDate "assignment.startAt")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.startDate"}}
                  </span>
                  <SortColumnIcon
                    @columnName="assignment.startAt"
                    @sortingPriorityQueue={{this.sortingPriorityQueue}}
                  />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 p-3"
                {{on "click" (fn this.processor.tableActions.sortByDate "assignment.endAt")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.dueDate"}}
                  </span>
                  <SortColumnIcon @columnName="assignment.endAt" @sortingPriorityQueue={{this.sortingPriorityQueue}} />
                </div>
              </th>
              <th
                class="bg-juicy-orange-100 p-3"
                {{on "click" (fn this.processor.tableActions.sortByValue "assignment.status")}}
                role="button"
              >
                <div class="flex flex-nowrap items-center gap-4">
                  <span>
                    {{t "assignments.headings.status"}}
                  </span>
                  <SortColumnIcon @columnName="assignment.status" @sortingPriorityQueue={{this.sortingPriorityQueue}} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-white">
            {{#each transformedData as |assignmentRow|}}
              <tr data-test-inner-assignments-table-row={{assignmentRow.assignment.id}}>
                <td class="bg-juicy-orange-50 p-3" title={{assignmentRow.assignment.id}} data-test-title>
                  {{#if assignmentRow.assignment.isLibraryAssignment}}
                    <ActivityDescriptionBook @assignment={{assignmentRow.assignment}} />
                  {{else}}
                    <ActivityDescriptionLesson @assignment={{assignmentRow.assignment}} />
                  {{/if}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left" data-test-assigned-by>
                  {{assignmentRow.assignerName}}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left" data-sort="date" data-test-assignment-start-date>
                  {{formatDate
                    assignmentRow.assignment.startAt
                    weekday="short"
                    day="2-digit"
                    month="short"
                    year="2-digit"
                  }}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left" data-sort="date" data-test-assignment-due-date>
                  {{formatDate
                    assignmentRow.assignment.endAt
                    weekday="short"
                    day="2-digit"
                    month="short"
                    year="2-digit"
                  }}
                </td>
                <td class="bg-juicy-orange-50 p-3 text-left" data-test-assignment-status>
                  <AssignmentStatus @status={{assignmentRow.studentStatus}} class="float-left" />
                </td>
              </tr>
            {{else}}
              <tr>
                <td class="bg-juicy-orange-50 p-3" {{colspanMax}}>
                  <div class="missing-assignments-data">
                    {{t "assignments.missingAssignmentsDataInList"}}
                  </div>
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      {{/let}}
    </div>
  </template>
}
